import exifr from "exifr";

function fillCanvasWhite(ctx, width, height) {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
}

/**
 * Read GPS coordinates from image EXIF when available.
 */
export async function getImageGps(file) {
  try {
    return await exifr.gps(file);
  } catch {
    return null;
  }
}

/**
 * Bake EXIF orientation into pixel data (for uploads before resize/compress).
 */
export async function normalizeImageFile(file) {
  if (!file || !file.type?.startsWith("image/")) return file;

  try {
    const orientation = await exifr.orientation(file);
    if (!orientation || orientation === 1) return file;

    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    fillCanvasWhite(ctx, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);

    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to normalize image orientation"));
            return;
          }
          resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }));
        },
        "image/jpeg",
        0.92,
      );
    });
  } catch {
    return file;
  }
}

/**
 * Shrink a gallery photo to at most `maxDimension` on its long side as a JPEG,
 * with EXIF orientation baked in.
 *
 * Phone photos are 3-10 MB at 12+ MP. Sending them whole took minutes on a
 * field connection, and re-encoding one on a full-size canvas could run iOS
 * Safari out of memory (the page went white). Decoding through an <img> lets
 * the browser apply EXIF orientation itself (drawImage honours it), and the
 * only canvas is the small output one. Falls back to normalizeImageFile when
 * the browser can't decode the file (HEIC on some Androids, say).
 */
export async function shrinkImageForUpload(file, maxDimension = 1600, quality = 0.85) {
  if (!file || !file.type?.startsWith("image/")) return file;

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) throw new Error("Image has no size");
    const ratio = Math.min(1, maxDimension / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * ratio);
    canvas.height = Math.round(h * ratio);
    const ctx = canvas.getContext("2d");
    fillCanvasWhite(ctx, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", quality);
    });
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) throw new Error("Failed to shrink image");
    // Already small and no smaller after re-encoding: keep the original.
    if (ratio === 1 && blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${name}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return normalizeImageFile(file);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function bitmapToObjectUrl(bitmap) {
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  fillCanvasWhite(ctx, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to render image"));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      "image/jpeg",
      0.92,
    );
  });
}

/**
 * Build a display URL that respects the image's true orientation.
 *
 * We bake EXIF orientation into the pixels when the file carries it, otherwise
 * we render the pixels exactly as stored. We deliberately do NOT guess
 * orientation from the aspect ratio — a previous "rotate anything landscape 90°"
 * heuristic flipped legitimately-landscape photos (and camera captures, which
 * are landscape canvas frames with no EXIF) onto their side.
 */
export async function getOrientedImageObjectUrl(src) {
  const response = await fetch(src);
  if (!response.ok) throw new Error("Failed to load image");
  const blob = await response.blob();

  const orientation = await exifr.orientation(blob).catch(() => 1);

  const bitmap =
    orientation && orientation !== 1
      ? await createImageBitmap(blob, { imageOrientation: "from-image" })
      : await createImageBitmap(blob);

  return bitmapToObjectUrl(bitmap);
}
